/**
 * 下单受理凭证（POST /orders/submission action:ticket）
 * 有效期约 15s；剩余约 5s 刷新；仅交易页前台预取；切后台/离开交易页停止；换钱包丢弃。
 * 下单瞬间仍可通过 ensureOrderSubmissionTicket 按需拉取。
 */
import { requestSubmissionTicket } from '@/api/orders';
import type { OrderSubmissionTicket } from '@/types/api';
import { getCurrentWalletAddress } from '@/common/services/request';
import logger from '@/common/utils/logger';

const REFRESH_REMAINING_MS = 5_000;
const MIN_SUBMIT_REMAINING_MS = 2_000;
/** 拉取失败后的退避，避免不可用时空转打 submission */
const FAIL_RETRY_MS = 30_000;

let cached: OrderSubmissionTicket | null = null;
let cachedWallet = '';
let cachedDeadline = 0;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<OrderSubmissionTicket | null> | null = null;
let running = false;
/** 合约 / AI 预测等可下单页引用计数 */
let tradeSurfaceCount = 0;

function walletKey(): string {
  return (getCurrentWalletAddress() || '').toLowerCase().trim();
}

function remainingMs(ticket: OrderSubmissionTicket | null): number {
  return ticket && ticket === cached ? cachedDeadline - performance.now() : 0;
}

function clearTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function discard() {
  cached = null;
  cachedWallet = '';
  cachedDeadline = 0;
  clearTimer();
}

function scheduleRefresh(waitMs?: number) {
  clearTimer();
  if (!running || tradeSurfaceCount <= 0) return;
  const wait =
    waitMs != null
      ? Math.max(1000, waitMs)
      : cached
        ? Math.max(500, remainingMs(cached) - REFRESH_REMAINING_MS)
        : FAIL_RETRY_MS;
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    if (!running || tradeSurfaceCount <= 0) return;
    void refreshTicket(true);
  }, wait);
}

async function fetchTicket(): Promise<OrderSubmissionTicket | null> {
  const wallet = walletKey();
  if (!wallet) {
    discard();
    return null;
  }
  try {
    const started = performance.now();
    const ticket = await requestSubmissionTicket({ expectedWallet: wallet });
    if (!ticket?.token || ticket.mode !== 'direct-v1') {
      // 签名未配置或返回异常：清空，走 prepare 回退；退避后再试
      if (walletKey() === wallet) discard();
      scheduleRefresh(FAIL_RETRY_MS);
      return null;
    }
    if (walletKey() !== wallet) return null;
    // Server TTL minus full RTT is conservative and independent of device wall-clock drift.
    const ttl = Date.parse(ticket.expiresAt) - Date.parse(ticket.serverTime) - (performance.now() - started);
    if (!Number.isFinite(ttl) || ttl < MIN_SUBMIT_REMAINING_MS) {
      scheduleRefresh(FAIL_RETRY_MS);
      return null;
    }
    cached = ticket;
    cachedDeadline = performance.now() + ttl;
    cachedWallet = wallet;
    scheduleRefresh();
    return ticket;
  } catch (e) {
    logger.warn('[OrderTicket] fetch failed', e);
    scheduleRefresh(FAIL_RETRY_MS);
    return null;
  }
}

async function refreshTicket(force = false): Promise<OrderSubmissionTicket | null> {
  const wallet = walletKey();
  if (!wallet) {
    discard();
    return null;
  }
  if (cached && cachedWallet === wallet && !force) {
    if (remainingMs(cached) > REFRESH_REMAINING_MS) {
      return cached;
    }
  }
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      return await fetchTicket();
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** 进入可下单页（合约 / AI）时调用；引用计数，避免多页叠打 */
export function enterOrderTicketSurface(): void {
  tradeSurfaceCount += 1;
  startOrderSubmissionTicket();
}

/** 离开可下单页时调用；全部离开才停预取 */
export function leaveOrderTicketSurface(): void {
  tradeSurfaceCount = Math.max(0, tradeSurfaceCount - 1);
  if (tradeSurfaceCount === 0) {
    stopOrderSubmissionTicket();
  }
}

/** 交易页前台 + 已登录时启动预取（非交易页不要调） */
export function startOrderSubmissionTicket(): void {
  if (tradeSurfaceCount <= 0) {
    // 允许下单瞬间 ensure 走 refresh，但不开启定时预取
    return;
  }
  running = true;
  const wallet = walletKey();
  if (!wallet) {
    discard();
    return;
  }
  // 软进入：缓存仍够用则复用，勿每次 onShow/hide 强制 POST submission
  void refreshTicket(false);
}

export function stopOrderSubmissionTicket(): void {
  running = false;
  clearTimer();
  inFlight = null;
}

/** 换钱包立即丢弃（不停引用计数；页面 leave 会收敛） */
export function discardOrderSubmissionTicket(): void {
  discard();
  inFlight = null;
  running = false;
  clearTimer();
}

/**
 * 下单前取得可用凭证；剩余不足则刷新。
 * 返回 null → 调用方走旧 prepare。
 */
export async function ensureOrderSubmissionTicket(): Promise<OrderSubmissionTicket | null> {
  const wallet = walletKey();
  if (!wallet) {
    discard();
    return null;
  }
  if (cached && cachedWallet === wallet && remainingMs(cached) >= MIN_SUBMIT_REMAINING_MS) {
    return cached;
  }
  return refreshTicket(true);
}

export function peekOrderSubmissionTicket(): OrderSubmissionTicket | null {
  const wallet = walletKey();
  if (!cached || !wallet || cachedWallet !== wallet) return null;
  if (remainingMs(cached) <= 0) return null;
  return cached;
}
